import classNames from 'classnames'
import { useState } from 'react'
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect'
import { useTheme } from '@opallabs/hooks'
import { InputLabel, Icon, Position } from '@opallabs/lapidary'
import {
  FieldDate,
  FieldDuration,
  FieldMultiSelect,
  FieldUsersSelect,
  FieldSingleSelect,
  FieldText,
  FieldURL,
} from './FieldInputs'
import * as styles from './styles'
import type {
  FieldMap,
  SharedFieldProps,
  GenericField,
  OnFieldChange,
} from './types'

const fieldMap: FieldMap = {
  date: FieldDate,
  duration: FieldDuration,
  multiSelect: FieldMultiSelect,
  singleSelect: FieldSingleSelect,
  textarea: FieldText,
  url: FieldURL,
  usersSelect: FieldUsersSelect,
}

export type FieldProps<TField extends GenericField> =
  SharedFieldProps<TField> & {
    className?: string
    iconGlyph?: string
    field: GenericField
    disabled?: boolean
    leadingNode?: React.ReactNode
    trailingNode?: React.ReactNode
  }

export const Field = <TField extends GenericField>({
  className,
  dateOptions,
  durationOptions,
  field,
  iconGlyph,
  leadingNode,
  notifications,
  trailingNode,
  onChange,
  onCascadeReset,
  readOnly,
  selectOptions,
  textOptions,
  valueLeadingNode,
  disabled,
}: FieldProps<TField>) => {
  const theme = useTheme()
  const [editing, setIsEditing] = useState(false)
  const [fieldData, setFieldData] = useState(field.data)
  const FieldInput = fieldMap[field.type]

  // Keep field data as internal state so the current value is available
  // more immediately than if we wait for ancestors to update the field prop
  const handleChange: OnFieldChange<TField> = (change) => {
    onChange(change)
    // The change names only the id and value, so the notification state comes
    // from the prop. Dropping it would blank a People Picker's notification
    // controls until the refetch lands.
    setFieldData({
      ...field.data,
      id: change.data?.id,
      value: change.data?.value ?? null,
    })
  }

  // The `field` object is typically not memoized, so any re-render
  // by the parent component would normally cause this effect to trigger.
  // That will have the effect of (from the user's POV) randomly resetting
  // the editing state, so to prevent that, we'll deep-compare `field.data`.
  useDeepCompareEffectNoCheck(() => {
    setFieldData(field.data)
    setIsEditing(false)
  }, [field.data])

  return (
    <div
      css={styles.fieldWrapper(theme, disabled)}
      className={classNames('lapidary-field', className)}
    >
      <InputLabel
        childrenPosition={Position.Start}
        css={styles.labelStyles}
        inputId={`field-${field.id}`}
        text={
          <>
            {field.displayName}
            {trailingNode}
          </>
        }
      >
        {leadingNode}
        {iconGlyph && <Icon glyph={iconGlyph} ariaHidden />}
      </InputLabel>
      <FieldInput
        editing={editing}
        colorsEnabled={!field.isColorDisabled}
        dateOptions={dateOptions}
        durationOptions={durationOptions}
        field={{ ...field, data: fieldData }}
        notifications={notifications}
        onChange={handleChange}
        onCascadeReset={onCascadeReset}
        readOnly={readOnly}
        setIsEditing={setIsEditing}
        selectOptions={selectOptions}
        textOptions={textOptions}
        valueLeadingNode={valueLeadingNode}
      />
    </div>
  )
}
